import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Fingerprint, Users, Lightbulb, Sparkles } from 'lucide-react';
import { SECTORS } from '../data/sectors';
import SubpageShell from './SubpageShell';
import SEO from '../components/SEO';
import RainbowText from '../components/RainbowText';
import '../styles/RainbowText.css';
import '../styles/AboutV2.css';

const VALUES = [
  { label: 'Inclusive by design', Icon: Fingerprint, tone: 'cyan' },
  { label: 'Family focused', Icon: Users, tone: 'violet' },
  { label: 'Innovation driven', Icon: Lightbulb, tone: 'amber' },
  { label: 'Lasting impact', Icon: Sparkles, tone: 'pink' },
];

const APPROACH = [
  'Infrastructure & Partnerships',
  'Experiences & Services',
  'Digital Access & Knowledge',
  'Family Support & Inclusion',
];

const CtaPair = ({ idPrefix }) => (
  <div className="ab-cta-row">
    <Link to="/infrastructure-enquiry" className="ab-btn-primary" data-testid={`${idPrefix}-partnership-cta`}>
      Start a partnership enquiry <ArrowRight size={16} strokeWidth={2.4} />
    </Link>
    <Link to="/developers-partners" className="ab-btn-ghost" data-testid={`${idPrefix}-partners-cta`}>
      Developers &amp; Partners <ArrowRight size={16} strokeWidth={2.4} />
    </Link>
  </div>
);

const About = () => (
  <SubpageShell currentPath="/about">
    <SEO
      title="About"
      description="N3URO WORLD® is Neuro-Inclusive Family Infrastructure — spaces, systems and experiences designed around family access, participation, regulation and connection."
      path="/about"
    />

    <section className="ab-hero" data-testid="about-hero">
      <figure className="ab-hero-media page-hero-image">
        <img src="/img/about/hero.webp" width="848" height="1264" alt="A parent and child walk hand in hand through a neuro-inclusive airport terminal at sunset" loading="eager" decoding="async" fetchPriority="high" />
      </figure>
      <div className="ab-hero-copy">
        <span className="ab-eyebrow">Neuro-Inclusive Family Infrastructure</span>
        <h1 className="ab-hero-title" data-testid="about-title">
          Spaces, Systems and Experiences <RainbowText>for Every Family.</RainbowText>
        </h1>
        <p className="ab-body">
          N3URO WORLD® is Neuro-Inclusive Family Infrastructure designed around family access,
          participation, regulation and connection. We build the spaces, partnerships and systems that
          enable families to navigate, access and participate in the environments they encounter — with
          neurological difference considered from the outset.
        </p>
        <p className="ab-body"><Link to="/what-is-n3uro-world" className="ab-inline-link" data-testid="about-what-is-link">What is N3URO WORLD® and what is live now?</Link></p>
        <CtaPair idPrefix="about-hero" />
      </div>
    </section>

    <section className="ab-section ab-eco" data-testid="about-ecosystem">
      <div className="ab-eco-copy">
        <span className="ab-eyebrow">A connected ecosystem</span>
        <h2 className="ab-h2">More than individual environments. <RainbowText>A connected ecosystem.</RainbowText></h2>
        <p className="ab-body">
          From individual environments to major developments and organisation-wide programmes, N3URO WORLD®
          is helping shape a future where neuro-inclusive family access becomes part of how places are
          planned, designed, operated and experienced.
        </p>
      </div>
      <ul className="ab-eco-grid">
        {SECTORS.map(({ slug, img, label, Icon }) => (
          <li key={img} className="ab-eco-tile" data-testid={`about-eco-${img}`}>
            <Link to={`/sectors/${slug}`} className="ab-eco-link" aria-label={`${label} — see what we offer`} data-testid={`about-eco-link-${slug}`}>
              <img src={`/img/about/${img}.webp`} width="600" height="600" alt="" loading="lazy" decoding="async" />
              <span className="ab-eco-label"><Icon size={22} strokeWidth={1.75} aria-hidden="true" />{label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>

    <section className="ab-section ab-real" data-testid="about-real-life">
      <figure className="ab-real-media">
        <img src="/img/about/kids.webp" width="1264" height="848" alt="Two smiling children wearing N3URO jackets" loading="lazy" decoding="async" />
      </figure>
      <div className="ab-real-copy">
        <span className="ab-eyebrow">N3URO WORLD®</span>
        <h2 className="ab-h2">Built for real life. <RainbowText>Designed for everyone.</RainbowText></h2>
        <p className="ab-body">
          We create infrastructure, experiences and resources that remove barriers and open up opportunity.
          Because neurodivergence isn&rsquo;t a limitation — it&rsquo;s a different way of experiencing the world.
        </p>
        <ul className="ab-values">
          {VALUES.map(({ label, Icon, tone }) => (
            <li key={label} className={`ab-value ab-value--${tone}`}>
              <Icon size={24} strokeWidth={1.6} aria-hidden="true" />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="ab-section ab-approach" id="model" data-testid="about-approach">
      <div className="ab-approach-copy">
        <span className="ab-eyebrow">Our approach</span>
        <h2 className="ab-h2">Creating access <RainbowText>across every journey.</RainbowText></h2>
        <p className="ab-body">
          We connect people, places and possibilities through an integrated ecosystem of infrastructure,
          partnerships, services and technology.
        </p>
        <ol className="ab-steps">
          {APPROACH.map((step, i) => (
            <li key={step} className={`ab-step ab-step--${i + 1}`}>
              <span className="ab-step-num">{String(i + 1).padStart(2, '0')}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
      <figure className="ab-approach-media">
        <img src="/img/about/lounge.webp" width="1264" height="848" alt="A parent and child look out over a city skyline from a calm airport lounge" loading="lazy" decoding="async" />
      </figure>
    </section>

    <section className="ab-section ab-close" data-testid="about-close">
      <span className="ab-eyebrow">Let&rsquo;s build a more inclusive tomorrow</span>
      <h2 className="ab-h2 ab-close-title"><RainbowText>Partnerships. Progress. Possibility.</RainbowText></h2>
      <p className="ab-body">
        Join us in shaping a future where every family has access, choice and opportunity — everywhere.
      </p>
      <CtaPair idPrefix="about-close" />
    </section>
  </SubpageShell>
);

export default About;
